import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import EPageHeader from '@/components/everridge/EPageHeader';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminBookingMainTab from '@/features/superAdmin/features/data/bookings/components/SuperAdminBookingMainTab';
import SuperAdminBookingEditDialog from '@/features/superAdmin/features/data/bookings/components/SuperAdminBookingEditDialog';
import SuperAdminBookingDeleteDialog from '@/features/superAdmin/features/data/bookings/components/SuperAdminBookingDeleteDialog';
import SuperAdminDataPaginatedTable from '@/features/superAdmin/features/data/shared/components/SuperAdminDataPaginatedTable';
import {
    saleForecastTransactionNestedColumns,
    saleTransactionNestedColumns,
} from '@/features/superAdmin/features/data/bookings/components/SuperAdminBookingsPageColumns';
import { useSuperAdminBookingDetailApi } from '@/features/superAdmin/features/data/bookings/hooks/useSuperAdminBookingDetailApi';
import { useSuperAdminBookingMutationsApi } from '@/features/superAdmin/features/data/bookings/hooks/useSuperAdminBookingMutationsApi';
import { UpdateSuperAdminBookingRequestDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type BookingDetailTab = 'main' | 'saleTransactions' | 'saleForecastTransactions';

const SuperAdminBookingDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { booking, isLoading } = useSuperAdminBookingDetailApi(id);
    const { updateMutation, deleteMutation } = useSuperAdminBookingMutationsApi();
    const [activeTab, setActiveTab] = useState<BookingDetailTab>('main');
    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    if (userRole !== Roles.superAdmin) return null;

    const goBack = () => navigate('/superAdmin/data/bookings');

    const tabs: { id: BookingDetailTab; label: string }[] = [
        { id: 'main', label: t('superAdmin.data.bookings.tabs.main') },
        { id: 'saleTransactions', label: t('superAdmin.data.nested.saleTransactions') },
        { id: 'saleForecastTransactions', label: t('superAdmin.data.nested.saleForecastTransactions') },
    ];

    const pageTitle = booking?.documentNumber
        ? `${t('superAdmin.data.bookings.detail.title')} — ${booking.documentNumber}`
        : t('superAdmin.data.bookings.detail.title');

    const handleSave = async (body: UpdateSuperAdminBookingRequestDto) => {
        if (!id) return;
        await updateMutation.mutateAsync({ id, body });
        EToast({ variant: 'success', message: t('superAdmin.data.bookings.toast.updated') });
    };

    const handleDelete = async () => {
        if (!id) return;
        await deleteMutation.mutateAsync(id);
        EToast({ variant: 'success', message: t('superAdmin.data.bookings.toast.deleted') });
        goBack();
    };

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={pageTitle}>
                <Button variant="outline" className="gap-2" onClick={goBack}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.bookings.backToList')}
                </Button>
                {booking && (
                    <>
                        <Button variant="outline" onClick={() => setShowEdit(true)}>
                            {t('superAdmin.data.bookings.actions.edit')}
                        </Button>
                        <Button variant="destructive" onClick={() => setShowDelete(true)}>
                            {t('superAdmin.data.bookings.actions.delete')}
                        </Button>
                    </>
                )}
            </EPageHeader>

            {isLoading && !booking ? (
                <div className="flex flex-1 items-center justify-center py-12">
                    <Loader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
            ) : !booking ? (
                <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6 text-sm">
                    {t('elements.table.body.empty')}
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2 border-b border-popover-100 pb-2">
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                variant={activeTab === tab.id ? 'default' : 'outline'}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>

                    {activeTab === 'main' && <SuperAdminBookingMainTab booking={booking} />}

                    {activeTab === 'saleTransactions' && (
                        <SuperAdminDataPaginatedTable
                            columns={saleTransactionNestedColumns}
                            data={booking.saleTransactions}
                        />
                    )}

                    {activeTab === 'saleForecastTransactions' && (
                        <SuperAdminDataPaginatedTable
                            columns={saleForecastTransactionNestedColumns}
                            data={booking.saleForecastTransactions}
                        />
                    )}

                    <SuperAdminBookingEditDialog
                        open={showEdit}
                        onOpenChange={setShowEdit}
                        booking={booking}
                        isMobile={isMobile}
                        isSaving={updateMutation.isPending}
                        onSave={handleSave}
                    />

                    <SuperAdminBookingDeleteDialog
                        open={showDelete}
                        onOpenChange={setShowDelete}
                        onConfirm={handleDelete}
                        isDeleting={deleteMutation.isPending}
                    />
                </>
            )}
        </section>
    );
};

export default SuperAdminBookingDetailPage;
